import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { Logo } from '../components/Logo';
import { IMAGES } from '../data/products';
import { useScreenInit } from '../useScreenInit.js';
type Tab = 'login' | 'register';
const fieldClass = 'w-full border border-white/10 bg-ink-800 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-pink-500 focus:outline-none';
const labelClass = 'block text-[10px] font-bold uppercase tracking-[0.2em] text-white/45';
function GoogleIcon({
  className = ''
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" aria-hidden focusable="false" data-id="element-1073" data-mp="2tnpgfgy761z1">
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.54 5.54 0 0 1-2.4 3.63v3h3.86c2.26-2.09 3.56-5.17 3.56-8.87z" data-id="element-1074" data-mp="2yma64mftp398" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z" data-id="element-1075" data-mp="3r3crlxtne95l" />
      <path fill="#FBBC05" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.29a11.99 11.99 0 0 0 0 10.76l3.98-3.09z" data-id="element-1076" data-mp="2mhze6c4zdzf2" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.7 0 3.99 2.47 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" data-id="element-1077" data-mp="3173zi272ojre" />
    </svg>;
}
function strengthOf(value: string): {
  score: number;
  label: string;
  color: string;
} {
  let score = 0;
  if (value.length >= 8) score += 1;
  if (/[A-Z]/.test(value)) score += 1;
  if (/[0-9]/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  if (!value) return {
    score: 0,
    label: '',
    color: 'bg-ink-500'
  };
  if (score <= 1) return {
    score: 1,
    label: 'Débil',
    color: 'bg-red-500'
  };
  if (score === 2) return {
    score: 2,
    label: 'Aceptable',
    color: 'bg-amber-500'
  };
  if (score === 3) return {
    score: 3,
    label: 'Buena',
    color: 'bg-teal-500'
  };
  return {
    score: 4,
    label: 'Excelente',
    color: 'bg-pink-500'
  };
}
export function Login() {
  const screenInit = useScreenInit();
  const [tab, setTab] = useState<Tab>(screenInit.tab as Tab ?? 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [news, setNews] = useState(true);
  const [remember, setRemember] = useState(true);
  const strength = useMemo(() => strengthOf(password), [password]);
  const tabs: {
    id: Tab;
    label: string;
  }[] = [{
    id: 'login',
    label: 'Iniciar sesión'
  }, {
    id: 'register',
    label: 'Crear cuenta'
  }];
  return <div className="relative flex min-h-screen w-full items-center justify-center bg-black px-5 py-14" data-id="element-1078" data-mp="3779z7afsi2sw">
      <img src={IMAGES.about} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-20" data-id="element-1079" data-mp="3c6wgbuyqo36z" />
      <div className="absolute inset-0 bg-black/80" data-id="element-1080" data-mp="3h2tsrq6vspeh" />

      <div className="relative w-full max-w-md" data-id="element-1081" data-mp="15ima089tutdr">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/45 transition-colors hover:text-white" data-id="element-1082" data-mp="10irhy1ebfxr4">
          <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-1083" data-mp="qklidqfzf3pf" />
          Volver a la tienda
        </Link>

        <main className="border border-white/10 bg-ink-800 p-7 sm:p-9" data-id="element-1084" data-mp="qmya2vbjksfo">
          <div className="flex justify-center" data-id="element-1085" data-mp="1o7kiyc9sovsl">
            <Logo size="md" data-id="element-1086" data-mp="1j8pq0hi45qur" />
          </div>
          <p className="mt-4 text-center text-sm text-white/45" data-id="element-1087" data-mp="1j9rdjlgbg5td">
            {tab === 'login' ? 'Ingresá para ver tus pedidos y comprar más rápido.' : 'Creá tu cuenta y entrená sin dolor con ZADAR.'}
          </p>

          {/* TABS */}
          <div role="tablist" aria-label="Acceso a la cuenta" className="mt-8 grid grid-cols-2 border-b border-white/10" data-id="element-1088" data-mp="26z3iua8ivtqe">
            {tabs.map(item => {
            const active = tab === item.id;
            return <button key={item.id} type="button" role="tab" aria-selected={active} onClick={() => setTab(item.id)} className={`relative pb-3.5 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${active ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-1089" data-mp="1rox5f90o4qrf">
                  {item.label}
                  <span className={`absolute -bottom-[1px] left-0 h-[2px] w-full transition-colors ${active ? 'bg-pink-500' : 'bg-transparent'}`} data-id="element-1090" data-mp="2ahrczhvypglm" />
                </button>;
          })}
          </div>

          {/* LOGIN */}
          {tab === 'login' && <form className="mt-8 space-y-5" onSubmit={e => e.preventDefault()} aria-label="Iniciar sesión" data-id="element-1091" data-mp="2t77m2tlpqhdn">
              <div data-id="element-1092" data-mp="csdp70r70ep5">
                <label className={labelClass} htmlFor="login-email" data-id="element-1093" data-mp="csvv6p3nq7a9">
                  Email
                </label>
                <input id="login-email" type="email" required autoComplete="email" placeholder="tu@email.com" className={`mt-2 ${fieldClass}`} data-id="element-1094" data-mp="rfp2p86xq8rw" />
              </div>

              <div data-id="element-1095" data-mp="3pzntk7ymkowo">
                <label className={labelClass} htmlFor="login-password" data-id="element-1096" data-mp="3q072iz6as2yk">
                  Contraseña
                </label>
                <div className="relative mt-2" data-id="element-1097" data-mp="7qoe5l3z0112">
                  <input id="login-password" type={showPassword ? 'text' : 'password'} required autoComplete="current-password" placeholder="••••••••" className={`${fieldClass} pr-12`} data-id="element-1098" data-mp="3qctw9fis2q4" />
                  <button type="button" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'} className="absolute right-1 top-1/2 -translate-y-1/2 p-3 text-white/40 transition-colors hover:text-white" data-id="element-1099" data-mp="bv5ml8f06luy">
                    {showPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-1100" data-mp="3u9eqbf58qmg8" /> : <EyeIcon className="h-4 w-4" data-id="element-1101" data-mp="3u7ai4l0b71a5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4" data-id="element-1102" data-mp="6xm601uu1wzu">
                <label className="flex cursor-pointer items-center gap-2.5 text-xs text-white/55" data-id="element-1103" data-mp="36kbzbgopvh92">
                  <input type="checkbox" checked={remember} onChange={() => setRemember(v => !v)} className="h-4 w-4 shrink-0 appearance-none border border-white/25 bg-transparent checked:border-pink-500 checked:bg-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500" data-id="element-1104" data-mp="3alfhi9u8lpdg" />
                  Recordarme
                </label>
                <a href="#" className="text-xs text-pink-400 transition-colors hover:text-pink-500 hover:underline" data-id="element-1105" data-mp="2p1byxvquzloo">
                  ¿Olvidaste tu contraseña?
                </a>
              </div>

              <button type="submit" className="w-full bg-pink-500 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-pink-600" data-id="element-1106" data-mp="2adh2q5dn6z6u">
                Iniciar sesión
              </button>

              <div className="flex items-center gap-4" data-id="element-1107" data-mp="21c13mtsa2jhx">
                <span className="h-[1px] flex-1 bg-white/10" data-id="element-1108" data-mp="1wd6dt7vw7uob" />
                <span className="text-xs uppercase text-white/35" data-id="element-1109" data-mp="1wg290ogcw592">o</span>
                <span className="h-[1px] flex-1 bg-white/10" data-id="element-1110" data-mp="3kaxfxqtzhyxg" />
              </div>

              <button type="button" className="flex w-full items-center justify-center gap-3 border border-white/20 py-3.5 text-xs font-semibold text-white transition-colors hover:border-white hover:bg-white/5" data-id="element-1111" data-mp="3p87blo7yv6tq">
                <GoogleIcon className="h-[18px] w-[18px]" data-id="element-1112" data-mp="31k684tpnagl6" />
                Continuar con Google
              </button>

              <p className="pt-1 text-center text-xs text-white/45" data-id="element-1113" data-mp="36awarz6os5c6">
                ¿No tenés cuenta?{' '}
                <button type="button" onClick={() => setTab('register')} className="font-semibold text-pink-400 transition-colors hover:text-pink-500 hover:underline" data-id="element-1114" data-mp="2sk9bo5x0kdfk">
                  Crear cuenta
                </button>
              </p>
            </form>}

          {/* REGISTER */}
          {tab === 'register' && <form className="mt-8 space-y-5" onSubmit={e => e.preventDefault()} aria-label="Crear cuenta" data-id="element-1115" data-mp="23z4ns8v4d53d">
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-1116" data-mp="2dw9f0n3tnqr1">
                <div data-id="element-1117" data-mp="2dxu8e9eeh3nb">
                  <label className={labelClass} htmlFor="reg-first" data-id="element-1118" data-mp="28xfxmjlezfll">
                    Nombre
                  </label>
                  <input id="reg-first" required autoComplete="given-name" placeholder="Camila" className={`mt-2 ${fieldClass}`} data-id="element-1119" data-mp="2mwpbvdq27zmx" />
                </div>
                <div data-id="element-1120" data-mp="2wov2ok4ksb0j">
                  <label className={labelClass} htmlFor="reg-last" data-id="element-1121" data-mp="2ro7b9xcfps5s">
                    Apellido
                  </label>
                  <input id="reg-last" required autoComplete="family-name" placeholder="Rodríguez" className={`mt-2 ${fieldClass}`} data-id="element-1122" data-mp="35oqkv8wjt6a2" />
                </div>
              </div>

              <div data-id="element-1123" data-mp="kc47obfqzy4g">
                <label className={labelClass} htmlFor="reg-email" data-id="element-1124" data-mp="kbm7x4tvn1rs">
                  Email
                </label>
                <input id="reg-email" type="email" required autoComplete="email" placeholder="tu@email.com" className={`mt-2 ${fieldClass}`} data-id="element-1125" data-mp="fdklqb15mmc3" />
              </div>

              <div data-id="element-1126" data-mp="133ezqvx7mgxx">
                <label className={labelClass} htmlFor="reg-password" data-id="element-1127" data-mp="130sqmruo7tdl">
                  Contraseña
                </label>
                <div className="relative mt-2" data-id="element-1128" data-mp="u27af3co8qfz">
                  <input id="reg-password" type={showNewPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" placeholder="Mínimo 8 caracteres" aria-describedby="password-strength" className={`${fieldClass} pr-12`} data-id="element-1129" data-mp="u1fj44q3xevi" />
                  <button type="button" onClick={() => setShowNewPassword(v => !v)} aria-label={showNewPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'} className="absolute right-1 top-1/2 -translate-y-1/2 p-3 text-white/40 transition-colors hover:text-white" data-id="element-1130" data-mp="23aab5szapc7q">
                    {showNewPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-1131" data-mp="2qzcz1ikiw1ao" /> : <EyeIcon className="h-4 w-4" data-id="element-1132" data-mp="rolpyqzuncx3" />}
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-3" data-id="element-1133" data-mp="wn5bwreg0opg">
                  <div className="flex flex-1 gap-1.5" aria-hidden data-id="element-1134" data-mp="1zx14bzx00zsx">
                    {[1, 2, 3, 4].map(step => <span key={step} className={`h-1 flex-1 transition-colors ${strength.score >= step ? strength.color : 'bg-ink-600'}`} data-id="element-1135" data-mp="1uzrfomhfos65" />)}
                  </div>
                  <span id="password-strength" className="w-20 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-white/50" data-id="element-1136" data-mp="2iuu4wvk5gxn5">
                    {strength.label || 'Seguridad'}
                  </span>
                </div>
              </div>

              <div data-id="element-1137" data-mp="2nnwy32l0m52t">
                <label className={labelClass} htmlFor="reg-confirm" data-id="element-1138" data-mp="31mwddjmn17mb">
                  Repetir contraseña
                </label>
                <input id="reg-confirm" type="password" required autoComplete="new-password" placeholder="••••••••" className={`mt-2 ${fieldClass}`} data-id="element-1139" data-mp="2wol357sfkv3k" />
              </div>

              <div className="space-y-3 pt-1" data-id="element-1140" data-mp="36bxgkr3my58f">
                <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-white/55" data-id="element-1141" data-mp="36b5xgfr85zz1">
                  <input type="checkbox" required checked={terms} onChange={() => setTerms(v => !v)} className="mt-0.5 h-4 w-4 shrink-0 appearance-none border border-white/25 bg-transparent checked:border-pink-500 checked:bg-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500" data-id="element-1142" data-mp="3bcbpebd29j7i" />
                  <span data-id="element-1143" data-mp="2ek83z9h1abci">
                    Acepto los{' '}
                    <a href="#" className="text-pink-400 hover:text-pink-500 hover:underline" data-id="element-1144" data-mp="2ekro8wu5pbtp">
                      términos y condiciones
                    </a>{' '}
                    y la política de privacidad.
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-white/55" data-id="element-1145" data-mp="26lwg1c34t1pr">
                  <input type="checkbox" checked={news} onChange={() => setNews(v => !v)} className="mt-0.5 h-4 w-4 shrink-0 appearance-none border border-white/25 bg-transparent checked:border-pink-500 checked:bg-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500" data-id="element-1146" data-mp="1wtqr6b86f0es" />
                  <span data-id="element-1147" data-mp="1iw24qyygnl7m">Quiero recibir ofertas y novedades por email.</span>
                </label>
              </div>

              <button type="submit" className="w-full bg-pink-500 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-pink-600" data-id="element-1148" data-mp="3d80a2ptnfwy8">
                Crear cuenta
              </button>

              <p className="pt-1 text-center text-xs text-white/45" data-id="element-1149" data-mp="3gpo2alil8v7a">
                ¿Ya tenés cuenta?{' '}
                <button type="button" onClick={() => setTab('login')} className="font-semibold text-pink-400 transition-colors hover:text-pink-500 hover:underline" data-id="element-1150" data-mp="3kpf0haag2dlu">
                  Iniciar sesión
                </button>
              </p>
            </form>}
        </main>
      </div>
    </div>;
}